import React, { useRef, useState } from "react";
import { Camera, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, imgUrl } from "../../lib/api";

export const PhotoUploader = ({ photos, onChange, max = 8 }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (files) => {
    const list = Array.from(files).slice(0, max - photos.length);
    if (!list.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const file of list) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/bloom/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(data.url);
      }
      onChange([...photos, ...urls]);
      toast.success(`${urls.length} photo${urls.length > 1 ? "s" : ""} loaded into the machine`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Photo upload failed — please try again");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div data-testid="photo-uploader">
      <div className="flex flex-wrap gap-3">
        {photos.map((url) => (
          <div key={url} className="relative w-20 h-20 rounded-2xl overflow-hidden border-2 border-plum-deep/15 group">
            <img src={imgUrl(url)} alt="Plant upload" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => onChange(photos.filter((p) => p !== url))}
              data-testid="photo-remove-btn"
              className="absolute top-1 right-1 w-5 h-5 rounded-full bg-plum-deep/80 text-cream flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              aria-label="Remove photo"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
        {photos.length < max && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            data-testid="photo-add-btn"
            className="w-20 h-20 rounded-2xl border-2 border-dashed border-magenta/50 text-magenta flex flex-col items-center justify-center gap-1 hover:bg-magenta/10 transition-colors disabled:opacity-50"
          >
            {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5" />}
            <span className="text-[9px] uppercase tracking-[0.12em] font-bold">{uploading ? "LOADING" : "ADD PHOTO"}</span>
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        multiple
        className="hidden"
        data-testid="photo-file-input"
        onChange={(e) => handleFiles(e.target.files)}
      />
    </div>
  );
};
